import { type FC, Suspense, useCallback, useEffect, useState } from "react";
import { Breakpoint, useMediaQuery } from "@stubhub/broadway-providers";
import { Container } from "@viagogo/core/ui";
import { RemoveFeature, useFeatureParticipationRecorder, useSimpleFeatureTernary } from "@viagogo/feature";
import type { VenueEventListViewModel } from "src/http/types";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { useAppContext } from "src/shared/context/AppContext";
import { lazyWithRetry } from "src/shared/utils/lazyWithRetry";
import type { Dates } from "src/types/shared/cookies";
import { VenueTrendingCarousel } from "src/website-apps/responsive/stubhub-venue/components/VenueTrendingCarousel";
import { useVenueTrendingEvents } from "src/website-apps/responsive/stubhub-venue/hooks/useVenueTrendingEvents";
import { EmailSignUp } from "src/website-apps/shared/components/email-signup";
import { FanProtectMessageBanner } from "src/website-apps/shared/components/fan-protect-message-banner/FanProtectMessageBanner";
import { FiltersAndGridsManager } from "src/website-apps/shared/components/FiltersAndGridsManager";
import { UserEventToastOnAncillaryPages } from "src/website-apps/shared/components/user-event-toast";
import { bodyWrapperStyle, mainColumnStyle, rightPanelStyle, twoColumnLayout, verticalMarginRowStyle } from "./styles";

const LazyVenueLocationMapCard = lazyWithRetry(() =>
  import("src/website-apps/responsive/stubhub-venue/components/VenueLocationMapCard/VenueLocationMapCard").then(
    (module) => ({
      default: module.VenueLocationMapCard,
    }),
  ),
);

interface Props {
  model: VenueEventListViewModel;
  dates?: Maybe<Dates>;
}

export const VenueLayoutV2Body: FC<Props> = ({ model, dates }) => {
  const appContext = useAppContext();
  const [hasTrendingContent, setHasTrendingContent] = useState(false);
  const handleHasTrendingContent = useCallback(() => setHasTrendingContent(true), []);
  const isDesktop = useMediaQuery(Breakpoint.Desktop);
  const { data: trendingEvents, isLoading: trendingPending } = useVenueTrendingEvents(model.venueId, dates);
  const hasTrendingEvents = Boolean(trendingEvents?.items?.length);
  // Variant-only render decision (record: false) so the removal arm never paints the carousel;
  // participation is recorded separately below, gated on the venue actually having trending events.
  const isTrendingCarouselRemoved =
    useSimpleFeatureTernary(FeatureSetEnum.TMVenueRemoveTrendingEventsCarousel, false, true, undefined, false) ?? false;
  const canRecordTrendingRemoval = useCallback(() => hasTrendingEvents, [hasTrendingEvents]);
  const recordTrendingRemovalParticipation = useFeatureParticipationRecorder(
    FeatureSetEnum.TMVenueRemoveTrendingEventsCarousel,
    canRecordTrendingRemoval,
  );
  useEffect(() => {
    if (hasTrendingEvents) {
      recordTrendingRemovalParticipation();
    }
  }, [hasTrendingEvents, recordTrendingRemovalParticipation]);
  const hasMapData =
    model.venueLatitude !== null &&
    model.venueLatitude !== undefined &&
    model.venueLongitude !== null &&
    model.venueLongitude !== undefined;

  return (
    <>
      <UserEventToastOnAncillaryPages userEventOption={model.userEventOption} />
      <div className={bodyWrapperStyle}>
        <Container>
          {!isTrendingCarouselRemoved && (
            <VenueTrendingCarousel
              venueName={model.venueName}
              events={trendingEvents}
              isLoading={trendingPending}
              onHasContent={handleHasTrendingContent}
            />
          )}
          <div className={twoColumnLayout({ trendingContent: hasTrendingContent ? "present" : "absent" })}>
            <div className={mainColumnStyle}>
              <FiltersAndGridsManager filterTypeOptions={model.gridFilterTypes} hideGridTitle />
              {!appContext.isNativeApp && (
                <div className={verticalMarginRowStyle}>
                  <EmailSignUp emailPrivacyDisclosureSiteMessage={model.privacyPolicyMessage} />
                </div>
              )}
            </div>
            {isDesktop && hasMapData && (
              <div className={rightPanelStyle}>
                <RemoveFeature
                  id={FeatureSetEnum.TMVenueDiscoveryRemoveVenuePageMap}
                  canParticipate={() => hasMapData}
                  feature={() => (
                    <Suspense fallback={<div style={{ height: "100%", width: "100%" }} />}>
                      <LazyVenueLocationMapCard
                        venueId={model.venueId}
                        venueAddress={model.address}
                        venueName={model.venueName}
                        latitude={model.venueLatitude!}
                        longitude={model.venueLongitude!}
                      />
                    </Suspense>
                  )}
                />
                <FanProtectMessageBanner />
              </div>
            )}
          </div>
        </Container>
      </div>
    </>
  );
};
